import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TemporaryChatContext } from '../Context/temporaryChatContext';
import chatService from '../services/chatService';
import sessionsService from '../services/sessionsServices';
import { processChatStream } from '../utils/processChatStream';
import { PLATFORM_ID } from '../constants/platform';
import { formatSessionHistory } from '../helpers/formatSessionHistory';

export function useChatSubmit({ createSession = false, sessionId } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    updateUserChat,
    webSearch,
    updateBotChat,
    setChatmessages,
    setSessionID,
    updateChatSource,
    selectedAgent,
    toggleRef,
    fakeAiLoader,
    updateStepsResultsChat,
    updateAllStepsLoading,
    finishStream,
    fakeAiLoaderClear,
    updateChatLoaderMessage,
    updateStepsChat,
    updateToolImages,
    updateDelegateBotChat,
    updateThinkingStepsLoading,
  } = useContext(TemporaryChatContext);

  const mutationCreateSession = useMutation({
    mutationFn: () =>
      sessionsService.createSession({
        platform_id: PLATFORM_ID,
        agent_id: selectedAgent?.value,
        project_id: 'test',
        mode: selectedAgent?.mode,
      }),
  });

  const streamHandlers = {
    updateDelegateBotChat,
    updateThinkingStepsLoading,
    updateChatLoaderMessage,
    updateBotChat,
    updateStepsChat,
    updateStepsResultsChat,
    updateToolImages,
    updateChatSource,
    updateAllStepsLoading,
    finishStream,
  };

  // After the stream ends, pull the canonical history so the reply's
  // intermediate_steps (with tool_result) are present immediately — this is
  // what powers the entity/action buttons below each reply. We keep the
  // streamed `ai` text to avoid a visual flash.
  const reconcileHistory = async sessionIdToUse => {
    if (!sessionIdToUse) return;
    try {
      const historyResp = await sessionsService.getSessionHistory({ session_id: sessionIdToUse });
      const formatted = formatSessionHistory(historyResp?.history);
      if (!formatted?.length) return;
      setChatmessages(prev =>
        prev.map((msg, i) =>
          formatted[i]
            ? {
                ...msg,
                intermediate_steps: formatted[i].intermediate_steps,
                source: formatted[i].source,
                files: formatted[i].files,
                images: formatted[i].images,
              }
            : msg,
        ),
      );
    } catch (e) {
      // Ignore — buttons fall back to appearing after a refresh.
    }
  };

  const submitMessage = async text => {
    const query = text?.trim();
    if (!query || isSubmitting) return false;

    // Only the URL session id is authoritative — never fall back to a stale context session.
    if (!createSession && !sessionId) {
      return false;
    }

    setIsSubmitting(true);
    setLoading(true);
    toggleRef(false);

    try {
      if (createSession) {
        setChatmessages([]);
        const sessionRes = await mutationCreateSession.mutateAsync();
        queryClient.invalidateQueries({ queryKey: ['sidebar_sessions'] });

        setSessionID(sessionRes?.session_id);
        updateUserChat(query);
        navigate(`/chat/${sessionRes?.session_id}`);

        fakeAiLoader();
        chatService.generateTitle({
          session_id: sessionRes?.session_id,
          query,
        });

        const aiResp = await chatService.createChat({
          platform_id: PLATFORM_ID,
          agent_id: selectedAgent?.value,
          session_id: sessionRes?.session_id,
          mode: selectedAgent?.mode,
          query,
          use_google_search: webSearch,
        });

        setLoading(false);
        await processChatStream(aiResp.body.getReader(), streamHandlers);
        await reconcileHistory(sessionRes?.session_id);
      } else {
        updateUserChat(query);
        fakeAiLoader();

        const aiResp = await chatService.createChat({
          platform_id: PLATFORM_ID,
          agent_id: selectedAgent?.value,
          session_id: sessionId,
          query,
          mode: selectedAgent?.mode,
          use_google_search: webSearch,
        });

        setLoading(false);
        await processChatStream(aiResp.body.getReader(), streamHandlers, { botChatSeparator: '\n \n' });
        await reconcileHistory(sessionId);
      }

      return true;
    } catch (error) {
      fakeAiLoaderClear();
      setLoading(false);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitMessage, isSubmitting, loading };
}
